import { faFileAlt } from '@fortawesome/pro-light-svg-icons'

import { DashboardModule, Link } from '@domain-group/suit-theme'

import EnabledPermission from '@components/EnabledPermission'

import useOrders from '@hooks/useOrders'
import { generateUrls } from '@hooks/useOrder'
import useSelector from '@hooks/useSelector'

import type { OrderRequestOptions, UserModel } from '@models/types'

import { getCampaignType } from '@functions/campaign'

import OrdersContainer from './OrdersContainer'

export const icon = faFileAlt

type ConfigOptionsParams = {
  currentUser?: UserModel,
  isAgent?: boolean,
  isCampaignManager?: boolean,
}

export const configOptions = (params: ConfigOptionsParams) => {
  const { currentUser, isAgent, isCampaignManager } = params

  const options = {
    title: {
      component: 'input',
      key: 'title',
      label: 'Title',
      optionKey: 'customization',
    },
    status: {
      component: 'select',
      key: 'status_group_key',
      label: 'Status',
      optionKey: 'queryParams',
      values: [
        { key: 'all', label: 'All' },
        { key: 'pending', label: 'Pending' },
        { key: 'complete', label: 'Complete' },
        { key: 'artwork_approved', label: 'Artwork Approved' },
        { key: 'artwork_pending', label: 'Artwork Pending' },
      ],
    },
    order_type: {
      component: 'select',
      key: 'order_type',
      label: 'Order Type',
      optionKey: 'queryParams',
      values: [
        { key: 'all', label: 'All' },
        { key: 'custom', label: 'Custom' },
        { key: 'editorial', label: 'Editorial' },
        { key: 'item', label: 'Item' },
        { key: 'package', label: 'Package' },
        { key: 'proposal', label: 'Proposal' },
        { key: 'publication', label: 'Publication' },
      ],
    },
    campaign_marketing_type: {
      component: 'select',
      key: 'campaign_marketing_type',
      label: 'Marketing Type',
      optionKey: 'queryParams',
      values: [
        { key: 'all', label: 'All' },
        { key: 'agency_marketing', label: 'Agency Marketing' },
        { key: 'property_marketing', label: 'Property Marketing' },
      ],
    },
    priority_status: {
      component: 'select',
      hidden: isCampaignManager || isAgent,
      key: 'priority_orders_by_user_id',
      label: 'Priority Status',
      optionKey: 'queryParams',
      values: [
        { key: 'all', label: 'All' },
        { key: currentUser?.id, label: 'Priority Only' },
      ],
    },
    display_mode: {
      component: 'select',
      key: 'display_mode',
      label: 'Display Mode',
      optionKey: 'customization',
      values: [
        { key: 'compact-list', label: 'List (Compact)' },
        { key: 'list', label: 'List (Large)' },
      ],
    },
    limit: {
      component: 'select',
      key: 'limit',
      label: 'Limit',
      optionKey: 'queryParams',
      values: [
        { key: 5, label: '5' },
        { key: 10, label: '10' },
        { key: 25, label: '25' },
        { key: 50, label: '50' },
      ],
    },
  }

  const filteredOptions = Object.fromEntries(
    // @ts-expect-error TS2339
    Object.entries(options).filter(([key, value]) => !value.hidden),
  )

  return filteredOptions
}

interface OrdersProps {
  customOptions: {
    moduleProps: {
      isCampaignManager: boolean,
    },
    queryParams: {
      campaign_id?: number,
      campaign_marketing_type?: string,
      limit?: number,
      order_type?: string,
      status_group_key?: string,
    },
    customization: {
      display_mode?: string,
      title?: string,
    },
    requestOptions: OrderRequestOptions,
  },
}

const Orders = (props: OrdersProps) => {
  const {
    customOptions,
    customOptions: {
      queryParams,
      requestOptions,
    },
  } = props

  const { campaign_id: campaignId } = queryParams

  const { customization, moduleProps } = customOptions
  const { display_mode, title } = customization || {}
  const { isCampaignManager } = moduleProps || {}

  const { loading } = useOrders()

  const { ordersOverviewUrl, pendingCampaignOrdersUrl } = generateUrls(
    {},
    { campaignId, prefixRoute: `campaigns/${campaignId}/edit` },
  )

  const url = isCampaignManager ? pendingCampaignOrdersUrl : ordersOverviewUrl

  const campaigns = useSelector(reduxState => reduxState.entities.campaigns)
  const campaign = campaignId && campaigns[campaignId] ? campaigns[campaignId] : {}

  return (
    <DashboardModule
      headerRight={(
        <EnabledPermission
          campaignType={getCampaignType(campaign)}
          permissionKey="campaign.orders.manage"
        >
          <Link href={url} textProps={{ fontSize: 'small' }}>
            Manage
          </Link>
        </EnabledPermission>
      )}
      loading={loading}
      title={title || 'Orders'}
    >
      {!loading && (
        <OrdersContainer
          displayMode={display_mode}
          queryParams={queryParams}
          isCampaignManager={isCampaignManager}
          requestOptions={requestOptions}
        />
      )}
    </DashboardModule>
  )
}

export default Orders
